import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { Button, Text } from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import type { RootStackParamList } from '~/navigation/types';
import { Components } from '~/scenes/HiredInsurance/components';
import { GENERAL_CONDITIONS_LINK } from '~/scenes/MarisaTakesCare/constants';
import { formatMoney } from '~/utils';
import DefaultContainer from '../../components/DefaultContainer';
import { useUserInfoStore } from '~/stores/userInfo';
import { isWeb } from '~/utils/device';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import { MARISA_TAKES_CARE_ACCESSION_TERMS_LINK } from '~/utils/url';

const HiredInsuranceDetailsTakesCare: React.FC = () => {
  const { goBack, navigate } = useAppNavigation();
  const contractedServices = useUserInfoStore(
    (state) => state.user?.contractedServices
  );

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<
        RootStackParamList,
        'MarisaServicos_HiredInsuranceDetailsTakesCare'
      >
    >();

  const contractedService = contractedServices?.find(
    (service) => service.certificateCode === certificateCode
  );

  const isCanceled = useMemo(
    () => Boolean(contractedService?.cancellationDate),
    [contractedService?.cancellationDate]
  );

  if (!contractedService) return null;

  return (
    <DefaultContainer type="marisa-cuida" className="p-6">
      <Text className="text-lg text-gray-700 mb-6">
        Detalhes da contratação
      </Text>

      <Components.InsuranceStatus data={contractedService} />

      {!!contractedService.holderName && (
        <>
          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Nome do Titular</Components.LeftText>
            <Components.RightText>
              {contractedService.holderName}
            </Components.RightText>
          </Components.Container>
        </>
      )}

      <Components.Divider />

      <Components.Container>
        <Components.LeftText>Valor mensal</Components.LeftText>
        <Text className="text-gray-700 text-sm">
          R${' '}
          <Text className="font-medium text-xl">
            {formatMoney(contractedService.value)}
          </Text>
          /mês
        </Text>
      </Components.Container>

      {!isCanceled && (
        <>
          <Components.Divider />

          <Components.Container>
            <Components.LeftText>
              Coberturas da Assistência Marisa Cuida
            </Components.LeftText>
            <Components.ChevronButton
              onPress={() =>
                navigate('MarisaServicos_HiredInsuranceCoverageTakesCare', {
                  id: certificateCode,
                })
              }
            />
          </Components.Container>
        </>
      )}

      <Components.Divider />

      <Components.Container>
        <Components.LeftText>Central de Atendimento</Components.LeftText>
        <Components.ChevronButton
          onPress={() =>
            navigate('MarisaServicos_HiredInsuranceCallCenterTakesCare', {
              id: certificateCode,
            })
          }
        />
      </Components.Container>

      <Components.Divider />

      {!isCanceled && (
        <>
          <Components.InsurancePolicyInfo
            policyNumber={contractedService.policyNumber}
            policyUrl={contractedService.policyUrl}
          />

          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Cancelamento</Components.LeftText>
            <Components.ChevronButton
              onPress={() =>
                navigate('MarisaServicos_HiredInsuranceCancelTakesCare', {
                  id: contractedService.certificateCode,
                })
              }
            />
          </Components.Container>

          <Components.Divider />
        </>
      )}

      <Text className="my-1 text-center text-xs text-gray-400">
        Condições gerais do Marisa Cuida:{' '}
        <Text
          className="font-medium text-primary-600"
          onPress={() => MarisaAppUtils.openUrl(GENERAL_CONDITIONS_LINK)}
        >
          Consulte aqui
        </Text>
      </Text>
      <Text className="text-center text-xs text-gray-400">
        Termos de adesão:{' '}
        <Text
          className="font-medium text-primary-600"
          onPress={() =>
            MarisaAppUtils.openUrl(MARISA_TAKES_CARE_ACCESSION_TERMS_LINK)
          }
        >
          Consulte aqui
        </Text>
      </Text>

      {!isWeb && <Components.Divider />}

      {isCanceled && (
        <Button
          onPress={() =>
            MarisaAppUtils.disableInsurance
              ? MarisaAppUtils.goToStoresList()
              : navigate('MarisaServicos_MarisaTakesCareContract', { step: 1 })
          }
          className="mt-3"
        >
          Contratar seguro novamente
        </Button>
      )}

      <Button
        variant="tertiary"
        className={isWeb ? 'mt-14' : 'mt-4'}
        onPress={goBack}
      >
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceDetailsTakesCare;
